import { logger } from '../../../utils/logger';
import { getCurrentUserId } from '../../../services/auth';

/** 同 tab 內通知所有 useLessonsProgress 重新讀取 */
export const LESSONS_PROGRESS_CHANGED_EVENT = 'orlo:lessons-progress-changed';

export type LessonId = 'lesson-1' | 'lesson-2' | 'lesson-3';

export type LessonStatus = 'not_started' | 'completed' | 'skipped';

export interface LessonProgress {
  status: LessonStatus;
  resolvedAt?: string; // ISO 8601，完成或跳過的時間
}

/** 舊版資料格式（boolean completed），讀取時轉換為三態 */
interface LegacyLessonProgress {
  completed: boolean;
  completedAt?: string;
}

export interface LessonsProgress {
  'lesson-1': LessonProgress;
  'lesson-2': LessonProgress;
  'lesson-3': LessonProgress;
}

const DEFAULT_PROGRESS: LessonsProgress = {
  'lesson-1': { status: 'not_started' },
  'lesson-2': { status: 'not_started' },
  'lesson-3': { status: 'not_started' },
};

function getStorageKey(): string {
  const userId = getCurrentUserId();
  return userId ? `orlo-lessons-progress-${userId}` : 'orlo-lessons-progress';
}

/** 將單個 lesson 的儲存資料（新格式或舊格式）轉換為三態 */
function normalizeLessonProgress(
  raw: Partial<LessonProgress & LegacyLessonProgress> | undefined
): LessonProgress {
  if (!raw) {
    return { status: 'not_started' };
  }
  if (raw.status) {
    return { status: raw.status, resolvedAt: raw.resolvedAt };
  }
  // 舊格式相容：completed: true → completed，completedAt 搬移至 resolvedAt
  if (raw.completed) {
    return { status: 'completed', resolvedAt: raw.completedAt };
  }
  return { status: 'not_started' };
}

/**
 * Lessons Service
 *
 * 管理 Onboarding Lessons 的進度狀態（localStorage 持久化）
 * 三態：not_started（未開始）/ completed（已完成）/ skipped（已跳過，不可逆）
 * Key 包含 userId，確保多帳號切換時各自獨立
 */
export const lessonsService = {
  /**
   * 取得所有 Lessons 的進度
   */
  getProgress(): LessonsProgress {
    try {
      const stored = localStorage.getItem(getStorageKey());
      if (!stored || stored === 'null') {
        return { ...DEFAULT_PROGRESS };
      }
      const parsed = JSON.parse(stored) as Partial<
        Record<LessonId, Partial<LessonProgress & LegacyLessonProgress>>
      >;
      return {
        'lesson-1': normalizeLessonProgress(parsed['lesson-1']),
        'lesson-2': normalizeLessonProgress(parsed['lesson-2']),
        'lesson-3': normalizeLessonProgress(parsed['lesson-3']),
      };
    } catch (error) {
      logger.warn('Failed to load lessons progress from localStorage', error);
      return { ...DEFAULT_PROGRESS };
    }
  },

  /**
   * 取得指定 Lesson 的三態狀態
   */
  getLessonStatus(lessonId: LessonId): LessonStatus {
    return this.getProgress()[lessonId].status;
  },

  /**
   * 標記指定 Lesson 為已完成
   */
  markCompleted(lessonId: LessonId): void {
    this.setStatus(lessonId, 'completed');
  },

  /**
   * 標記指定 Lesson 為已跳過（單堂課跳過，不可逆）
   */
  markSkipped(lessonId: LessonId): void {
    this.setStatus(lessonId, 'skipped');
  },

  /**
   * 將所有尚未開始（not_started）的 Lesson 一次性標記為已跳過
   */
  markAllSkipped(): void {
    const progress = this.getProgress();
    (Object.keys(progress) as LessonId[]).forEach((lessonId) => {
      this.setStatus(lessonId, 'skipped');
    });
  },

  /**
   * 寫入指定 Lesson 的狀態
   * completed / skipped 皆為終結狀態，一旦寫入即不可被覆寫（不可逆）
   */
  setStatus(lessonId: LessonId, status: LessonStatus): void {
    try {
      const progress = this.getProgress();
      const currentStatus = progress[lessonId].status;
      if (currentStatus === 'completed' || currentStatus === 'skipped') {
        return;
      }
      const updated = {
        ...progress,
        [lessonId]: { status, resolvedAt: new Date().toISOString() },
      };
      localStorage.setItem(getStorageKey(), JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent(LESSONS_PROGRESS_CHANGED_EVENT));
    } catch (error) {
      logger.error('Failed to save lesson progress to localStorage', error);
    }
  },

  /**
   * 檢查指定 Lesson 是否已完成
   * 嚴格語意：只有 status === 'completed' 才回傳 true，不涵蓋 skipped
   */
  isCompleted(lessonId: LessonId): boolean {
    return this.getProgress()[lessonId].status === 'completed';
  },

  /**
   * 檢查所有 Lessons 是否全部完成
   */
  isAllCompleted(): boolean {
    const progress = this.getProgress();
    return (
      progress['lesson-1'].status === 'completed' &&
      progress['lesson-2'].status === 'completed' &&
      progress['lesson-3'].status === 'completed'
    );
  },

  /**
   * 重設所有 Lessons 進度（開發/測試用）
   */
  resetAll(): void {
    try {
      localStorage.removeItem(getStorageKey());
    } catch (error) {
      logger.error('Failed to reset lessons progress in localStorage', error);
    }
  },
};
